import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * 테마 컨텍스트
 * - 다크/라이트 모드 지원
 * - 시스템 테마 자동 감지
 * - 사용자 설정 저장
 */

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme은 ThemeProvider 내에서 사용해야 합니다');
  }
  return context;
};

// 테마 설정 값들
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

// 시스템 테마 감지
const getSystemTheme = () => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? THEMES.DARK 
      : THEMES.LIGHT;
  }
  return THEMES.LIGHT;
};

// 로컬 스토리지에서 테마 설정 불러오기
const getSavedTheme = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('newpass2-theme');
    return saved && Object.values(THEMES).includes(saved) ? saved : THEMES.SYSTEM;
  }
  return THEMES.SYSTEM;
};

// 실제 적용할 테마 계산
const getEffectiveTheme = (themeMode) => {
  if (themeMode === THEMES.SYSTEM) {
    return getSystemTheme();
  }
  return themeMode;
};

export function ThemeProvider({ children }) {
  const [themeMode, setThemeMode] = useState(getSavedTheme);
  const [effectiveTheme, setEffectiveTheme] = useState(() => 
    getEffectiveTheme(getSavedTheme())
  );

  // 테마 변경 함수
  const changeTheme = (newTheme) => {
    if (!Object.values(THEMES).includes(newTheme)) {
      console.warn(`Invalid theme: ${newTheme}`);
      return;
    }
    
    setThemeMode(newTheme);
    localStorage.setItem('newpass2-theme', newTheme);
  };

  // 다음 테마로 순환
  const toggleTheme = () => {
    const themes = [THEMES.LIGHT, THEMES.DARK, THEMES.SYSTEM];
    const currentIndex = themes.indexOf(themeMode);
    const nextIndex = (currentIndex + 1) % themes.length;
    changeTheme(themes[nextIndex]);
  };

  // 시스템 테마 변경 감지
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = () => {
      if (themeMode === THEMES.SYSTEM) {
        setEffectiveTheme(getSystemTheme());
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [themeMode]);

  // themeMode 변경시 effectiveTheme 업데이트
  useEffect(() => {
    setEffectiveTheme(getEffectiveTheme(themeMode));
  }, [themeMode]);

  // DOM에 테마 클래스 적용
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    // 기존 테마 클래스 제거
    root.classList.remove('theme-light', 'theme-dark');
    body.classList.remove('theme-light', 'theme-dark');
    
    // 새 테마 클래스 추가
    const themeClass = `theme-${effectiveTheme}`;
    root.classList.add(themeClass);
    body.classList.add(themeClass);
    
    // 메타 태그 업데이트 (모바일 상태바 색상)
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.content = effectiveTheme === THEMES.DARK ? '#1f2937' : '#ffffff';
    }
  }, [effectiveTheme]);

  const value = {
    // 현재 상태
    themeMode,           // 사용자가 선택한 테마 (light/dark/system)
    effectiveTheme,      // 실제로 적용되는 테마 (light/dark)
    isDark: effectiveTheme === THEMES.DARK,
    isLight: effectiveTheme === THEMES.LIGHT,
    isSystemMode: themeMode === THEMES.SYSTEM,
    
    // 테마 변경 함수들
    changeTheme,
    toggleTheme,
    setLightTheme: () => changeTheme(THEMES.LIGHT),
    setDarkTheme: () => changeTheme(THEMES.DARK),
    setSystemTheme: () => changeTheme(THEMES.SYSTEM),
    
    // 테마 정보
    themes: THEMES,
    systemTheme: getSystemTheme()
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;
export { ThemeContext };